import { CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import type { BookmarkedUrl } from '../../../../types/bookmark'

/**
 * 조밀한 카드.
 *
 * 한 화면에 많이 담는 것이 목적이라 제목·코드·클릭 수만 둔다. 태그나
 * 미리보기까지 넣으면 카드 보기와 다를 바가 없어진다.
 */

interface CompactUrlCardProps {
  url: BookmarkedUrl
  isSelected: boolean
  isSelectionMode: boolean
  onToggleSelect: (urlId: string) => void
  onCopySuccess: (text: string) => void
  /** 카드를 눌러 상세 패널을 연다 */
  onOpenDetail?: (urlId: string) => void
}

export default function CompactUrlCard({
  url,
  isSelected,
  isSelectionMode,
  onToggleSelect,
  onCopySuccess,
  onOpenDetail,
}: CompactUrlCardProps) {
  const { t } = useTranslation()
  const code = url.customCode || url.shortCode
  const shortUrl = url.shortUrl || `https://shorty.kr/${code}`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shortUrl)
      onCopySuccess(shortUrl)
    } catch (error) {
      console.error('클립보드 복사 실패:', error)
    }
  }

  return (
    <article
      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 transition ${
        isSelected
          ? 'border-primary-400 bg-primary-50 dark:border-primary-600 dark:bg-primary-950/40'
          : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      {isSelectionMode && (
        <button
          type="button"
          role="checkbox"
          aria-checked={isSelected}
          aria-label={t('bookmark.selectItem')}
          onClick={() => onToggleSelect(url._id)}
          className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border-[1.5px] transition ${
            isSelected
              ? 'border-primary-600 bg-primary-600 text-white'
              : 'border-gray-300 dark:border-gray-500'
          }`}
        >
          {isSelected && <CheckIcon className="h-3 w-3" />}
        </button>
      )}

      <button
        type="button"
        onClick={() => onOpenDetail?.(url._id)}
        className="flex min-w-0 flex-1 flex-col text-left"
      >
        <span className="truncate text-[13px] font-bold text-gray-900 dark:text-white">
          {url.title || code}
        </span>
        <span className="truncate text-xs font-semibold text-primary-600 dark:text-primary-400">
          /{code}
        </span>
      </button>

      <span className="shrink-0 text-xs font-bold tabular-nums text-gray-600 dark:text-gray-300">
        {url.clickCount.toLocaleString()}
      </span>

      <button
        type="button"
        onClick={copy}
        aria-label={t('bookmark.copyShortUrl')}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white"
      >
        <DocumentDuplicateIcon className="h-4 w-4" />
      </button>
    </article>
  )
}
